import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';

export default function ExportPreviewModal({ open, onClose, previewText }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  
  // Draggable modal state
  const [dragging, setDragging] = useState(false);
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0 });
  const mouseStart = useRef({ x: 0, y: 0 });
  
  // Parse Taurus format text
  const [parsedData, setParsedData] = useState([]);
  
  useEffect(() => {
    if (previewText) {
      parseTaurusText(previewText);
    }
  }, [previewText]);
  
  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e) => {
      setModalPos({
        x: dragStart.current.x + (e.clientX - mouseStart.current.x),
        y: dragStart.current.y + (e.clientY - mouseStart.current.y),
      });
    };
    const handleMouseUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);
  
  const parseTaurusText = (text) => {
    if (!text) {
      setParsedData([]);
      return;
    }
    
    const lines = text.split('\n').filter(line => line.trim() !== '');
    const parsed = lines.map(line => {
      // Split by semicolon and remove quotes
      const values = line.split(';').map(value => {
        // Remove surrounding quotes if present
        let cleaned = value.trim();
        if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
          cleaned = cleaned.slice(1, -1);
        }
        return cleaned;
      });
      return values;
    });
    
    setParsedData(parsed);
  };
  
  // Drag handlers
  const onHeaderMouseDown = (e) => {
    setDragging(true);
    dragStart.current = { ...modalPos };
    mouseStart.current = { x: e.clientX, y: e.clientY };
  };
  
  if (!open) return null;
  
  // Determine max columns across all rows
  const maxCols = parsedData.length > 0 
    ? Math.max(...parsedData.map(row => row.length))
    : 0;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(30,41,59,0.18)',
      zIndex: 10002,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      userSelect: dragging ? 'none' : undefined,
    }}>
      <div
        style={{
          background: colors.card,
          borderRadius: 12,
          boxShadow: isDark ? '0 4px 32px rgba(0,0,0,0.5)' : '0 4px 32px rgba(30,41,59,0.18)',
          padding: 32,
          minWidth: 800,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'absolute',
          left: `calc(50% + ${modalPos.x}px)`,
          top: `calc(50% + ${modalPos.y}px)`,
          transform: 'translate(-50%, -50%)',
          cursor: dragging ? 'grabbing' : 'default',
        }}
      >
        <div
          style={{
            marginTop: 0,
            marginBottom: 18,
            color: colors.primary,
            fontSize: 24,
            fontWeight: 700,
            cursor: 'grab',
            userSelect: 'none',
            borderBottom: `2px solid ${colors.border}`,
            paddingBottom: 8,
            marginLeft: -32,
            marginRight: -32,
            paddingLeft: 32,
            paddingRight: 32,
            background: colors.card,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          onMouseDown={onHeaderMouseDown}
        >
          <span>{t('exportPreviewModal.title') || 'Export Preview'}</span>
        </div>
        
        {parsedData.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.textSecondary }}>
            {t('exportPreviewModal.noData') || 'No data to preview'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(90vh - 200px)' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  {Array.from({ length: maxCols }, (_, i) => (
                    <th 
                      key={i} 
                      style={{ 
                        padding: 8, 
                        border: `1px solid ${colors.border}`, 
                        background: colors.backgroundHover, 
                        color: colors.text,
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        minWidth: 120,
                        textAlign: 'left'
                      }}
                    >
                      {t('exportPreviewModal.column') || 'Column'} {i + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsedData.map((row, i) => (
                  <tr key={i}>
                    {Array.from({ length: maxCols }, (_, j) => (
                      <td 
                        key={j} 
                        style={{ 
                          border: `1px solid ${colors.border}`, 
                          padding: 8, 
                          color: colors.text,
                          background: colors.card,
                          wordBreak: 'break-word',
                          maxWidth: 200,
                        }}
                      >
                        {row[j] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18 }}>
          <button 
            onClick={onClose} 
            style={{ 
              padding: '8px 18px', 
              borderRadius: 6, 
              border: `1px solid ${colors.border}`, 
              background: colors.backgroundHover, 
              color: colors.text, 
              fontWeight: 600, 
              fontSize: 15, 
              cursor: 'pointer' 
            }}
          >
            {t('exportPreviewModal.close') || 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}

